import { useEffect, useRef, useState } from 'react';
import { apiGet, apiJson } from '../api/client';
import { playAudio } from '../hooks/useAudioPlayer';
import { VOICES } from '../constants/voices';
import {
  validateVoicePreset,
  exampleVoicePreset,
  VoicePreset,
  SpeakerConfigPreset,
  DirectorNotesPreset,
} from '../voice-preset/schema';

const API_BASE = import.meta.env.VITE_API_BASE as string;

interface PresetListItem {
  id: string;
  name: string;
  base_voice: string;
  preset: VoicePreset;
  created_at: number;
  updated_at: number;
}

interface PresetListResponse {
  presets: PresetListItem[];
}

interface SettingsResponse {
  settings: Record<string, string>;
}

interface CreateResponse {
  id: string;
  preset: VoicePreset;
}

interface SpeakResponse {
  id: string;
  r2Key: string;
  signedUrl: string;
  sizeBytes: number;
  durationMs: number;
  voice: string;
  model: string;
}

type PronRow = { token: string; hint: string };
type SpeakerRow = { name: string; voice: string; director: string };

function pronToRows(rec: Record<string, string> | undefined): PronRow[] {
  if (!rec) return [];
  return Object.entries(rec).map(([token, hint]) => ({ token, hint }));
}

function rowsToPron(rows: PronRow[]): Record<string, string> | undefined {
  const rec: Record<string, string> = {};
  for (const r of rows) {
    const k = r.token.trim();
    const v = r.hint.trim();
    if (!k || !v) continue;
    rec[k] = v;
  }
  return Object.keys(rec).length > 0 ? rec : undefined;
}

function speakersToRows(sc: SpeakerConfigPreset[] | undefined): SpeakerRow[] {
  if (!sc) return [];
  return sc.map((s) => ({
    name: s.name ?? '',
    voice: s.voice ?? '',
    director: s.director ?? '',
  }));
}

function rowsToSpeakers(rows: SpeakerRow[]): SpeakerConfigPreset[] | undefined {
  const out: SpeakerConfigPreset[] = [];
  for (const r of rows) {
    const n = r.name.trim();
    const v = r.voice.trim();
    if (!n || !v) continue;
    const entry: SpeakerConfigPreset = { name: n, voice: v };
    if (r.director.trim()) entry.director = r.director.trim();
    out.push(entry);
  }
  // Per the validator and skill convention: length < 2 is stripped entirely,
  // so single-speaker mode applies.
  return out.length >= 2 ? out : undefined;
}

function slugify(s: string): string {
  const base = s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return base || 'voice-preset';
}

export function VoicePresetsPanel({ onChanged }: { onChanged: () => void }) {
  const [presets, setPresets] = useState<PresetListItem[] | null>(null);
  const [activeId, setActiveId] = useState<string>('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [importErrors, setImportErrors] = useState<string[] | null>(null);
  const [testing, setTesting] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<VoicePreset | null>(null);
  const [draftErrors, setDraftErrors] = useState<string[]>([]);

  const refresh = async (): Promise<void> => {
    setErr(null);
    try {
      const list = await apiGet<PresetListResponse>('/api/voice-presets');
      setPresets(list.presets);
      const settings = await apiGet<SettingsResponse>('/api/settings');
      setActiveId(settings.settings.default_voice_preset_id ?? '');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const setActive = async (id: string): Promise<void> => {
    setBusy(true);
    setErr(null);
    setActiveId(id);
    try {
      await apiJson('PUT', '/api/settings', {
        default_voice_preset_id: id,
      });
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onImportFile = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    setImportErrors(null);
    setErr(null);
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const text = await file.text();
      let parsed: unknown;
      try {
        parsed = JSON.parse(text);
      } catch (err2) {
        setImportErrors([
          `Invalid JSON: ${err2 instanceof Error ? err2.message : String(err2)}`,
        ]);
        return;
      }
      const result = validateVoicePreset(parsed);
      if (!result.ok) {
        setImportErrors(result.errors);
        return;
      }
      await apiJson<CreateResponse>('POST', '/api/voice-presets', {
        preset: result.preset,
      });
      await refresh();
      onChanged();
    } catch (err3) {
      setErr(err3 instanceof Error ? err3.message : String(err3));
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const deletePreset = async (id: string): Promise<void> => {
    if (editingId === id) {
      const proceed = window.confirm(
        'Discard unsaved changes and delete this preset?',
      );
      if (!proceed) return;
      setEditingId(null);
      setDraft(null);
      setDraftErrors([]);
    }
    setBusy(true);
    setErr(null);
    try {
      await apiJson('DELETE', `/api/voice-presets/${encodeURIComponent(id)}`);
      if (activeId === id) {
        await apiJson('PUT', '/api/settings', { default_voice_preset_id: '' });
        setActiveId('');
      }
      await refresh();
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const testPreset = async (item: PresetListItem): Promise<void> => {
    setTesting(item.id);
    setErr(null);
    try {
      const res = await apiJson<SpeakResponse>('POST', '/api/voice/speak', {
        text: `Hello, I am ${item.preset.name}.`,
        presetId: item.id,
      });
      const absolute = res.signedUrl.startsWith('http')
        ? res.signedUrl
        : `${API_BASE}${res.signedUrl}`;
      await playAudio(absolute);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setTesting(null);
    }
  };

  const downloadExample = (): void => {
    const blob = new Blob([JSON.stringify(exampleVoicePreset(), null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'voice-preset-example.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const startEdit = (item: PresetListItem): void => {
    setEditingId(item.id);
    setDraft(JSON.parse(JSON.stringify(item.preset)) as VoicePreset);
    setDraftErrors([]);
  };

  const cancelEdit = (): void => {
    setEditingId(null);
    setDraft(null);
    setDraftErrors([]);
  };

  const saveDraft = async (): Promise<void> => {
    if (!editingId || !draft) return;
    const result = validateVoicePreset(draft);
    if (!result.ok) {
      setDraftErrors(result.errors);
      return;
    }
    setBusy(true);
    setDraftErrors([]);
    setErr(null);
    try {
      await apiJson<CreateResponse>('POST', '/api/voice-presets', {
        id: editingId,
        preset: result.preset,
      });
      setEditingId(null);
      setDraft(null);
      await refresh();
      onChanged();
    } catch (e) {
      setDraftErrors([e instanceof Error ? e.message : String(e)]);
    } finally {
      setBusy(false);
    }
  };

  const exportPreset = (item: PresetListItem): void => {
    const slug = slugify(item.preset.name);
    const blob = new Blob([JSON.stringify(item.preset, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${slug}.voice-profile.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Live debounced client-side validation while editing.
  useEffect(() => {
    if (!draft) return;
    const handle = setTimeout(() => {
      const result = validateVoicePreset(draft);
      setDraftErrors(result.ok ? [] : result.errors);
    }, 300);
    return () => clearTimeout(handle);
  }, [draft]);

  // Esc anywhere inside the panel cancels edit mode.
  useEffect(() => {
    if (editingId === null) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') cancelEdit();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [editingId]);

  return (
    <div className="panel">
      <h3>Voice Presets</h3>
      <p className="muted small">
        Import JSON presets that pre-configure base voice, director notes
        (tone, pace, accent, energy, reverb, emotion, pronunciation), and
        optional multi-speaker layouts. When a preset is active, every TTS
        turn uses it automatically.
      </p>

      <div className="voice-dropdown-row">
        <label className="muted small" htmlFor="vp-active">
          Active preset:
        </label>
        <select
          id="vp-active"
          value={activeId}
          onChange={(e) => void setActive(e.target.value)}
          disabled={busy || presets === null}
        >
          <option value="">None</option>
          {(presets ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} — {p.base_voice}
            </option>
          ))}
        </select>
      </div>

      <div className="row" style={{ marginTop: 12, gap: 8, flexWrap: 'wrap' }}>
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={busy}
        >
          Import JSON
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/json,.json"
          onChange={(e) => void onImportFile(e)}
          disabled={busy}
          style={{ display: 'none' }}
        />
        <button onClick={downloadExample} disabled={busy}>
          Download example
        </button>
      </div>

      {importErrors && importErrors.length > 0 ? (
        <div className="status-missing" style={{ marginTop: 8 }}>
          <div>Preset JSON is invalid:</div>
          <ul>
            {importErrors.map((m, i) => (
              <li key={i}>{m}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <h4 style={{ marginTop: 16 }}>Saved presets</h4>
      {presets === null ? (
        <div className="muted">Loading…</div>
      ) : presets.length === 0 ? (
        <div className="muted small">
          No presets yet. Import a JSON file above to add one.
        </div>
      ) : (
        <ul className="voice-list">
          {presets.map((p) => (
            <li key={p.id} className="voice-list-item">
              <div className="voice-list-row">
                <span className="voice-list-name">{p.name}</span>
                <span className="muted small">{p.base_voice}</span>
                <button
                  className="btn-test-voice"
                  onClick={() => void testPreset(p)}
                  disabled={testing !== null}
                  aria-label={`Test preset ${p.name}`}
                  title="Play this preset saying its name"
                >
                  {testing === p.id ? 'Playing…' : 'Test'}
                </button>
                {editingId === p.id ? (
                  <button
                    onClick={cancelEdit}
                    disabled={busy}
                    aria-label={`Cancel editing ${p.name}`}
                  >
                    Cancel
                  </button>
                ) : (
                  <button
                    onClick={() => startEdit(p)}
                    disabled={busy || editingId !== null}
                    aria-label={`Edit preset ${p.name}`}
                    title="Edit this preset"
                  >
                    Edit
                  </button>
                )}
                <button
                  onClick={() => exportPreset(p)}
                  disabled={busy}
                  aria-label={`Export preset ${p.name} as JSON`}
                  title="Download this preset as JSON"
                >
                  Export
                </button>
                <button
                  onClick={() => void deletePreset(p.id)}
                  disabled={busy}
                  aria-label={`Delete preset ${p.name}`}
                >
                  Delete
                </button>
              </div>
              {editingId === p.id && draft ? (
                <EditForm
                  draft={draft}
                  onChange={setDraft}
                  errors={draftErrors}
                  onSave={() => void saveDraft()}
                  onCancel={cancelEdit}
                  busy={busy}
                  presetName={p.name}
                />
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {err ? (
        <div className="status-missing" style={{ marginTop: 8 }}>
          Error: {err}
        </div>
      ) : null}
    </div>
  );
}

// ---------- Inline editor subcomponent ----------

interface EditFormProps {
  draft: VoicePreset;
  onChange: (next: VoicePreset) => void;
  errors: string[];
  onSave: () => void;
  onCancel: () => void;
  busy: boolean;
  presetName: string;
}

function EditForm({
  draft,
  onChange,
  errors,
  onSave,
  onCancel,
  busy,
  presetName,
}: EditFormProps) {
  const nameRef = useRef<HTMLInputElement | null>(null);

  // Local UI state for pronunciation rows and speaker rows; folded back
  // into draft on every change so live validation sees the current shape.
  const [pronRows, setPronRows] = useState<PronRow[]>(() =>
    pronToRows(draft.director_notes?.pronunciation),
  );
  const [speakerRows, setSpeakerRows] = useState<SpeakerRow[]>(() =>
    speakersToRows(draft.speaker_configs),
  );

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  // Helpers that produce a shallow-cloned draft with a single path updated.
  const update = (mutator: (d: VoicePreset) => VoicePreset): void => {
    onChange(mutator(draft));
  };

  const updateDN = (
    field: keyof DirectorNotesPreset,
    value: string,
  ): void => {
    update((d) => {
      const dn: DirectorNotesPreset = { ...(d.director_notes ?? {}) };
      if (value.trim().length === 0) {
        if (field in dn) delete (dn as Record<string, unknown>)[field];
      } else {
        (dn as Record<string, unknown>)[field] = value;
      }
      return { ...d, director_notes: Object.keys(dn).length > 0 ? dn : undefined };
    });
  };

  const commitPronRows = (rows: PronRow[]): void => {
    setPronRows(rows);
    update((d) => {
      const dn: DirectorNotesPreset = { ...(d.director_notes ?? {}) };
      const rec = rowsToPron(rows);
      if (rec) dn.pronunciation = rec;
      else delete dn.pronunciation;
      return { ...d, director_notes: Object.keys(dn).length > 0 ? dn : undefined };
    });
  };

  const commitSpeakerRows = (rows: SpeakerRow[]): void => {
    setSpeakerRows(rows);
    update((d) => {
      const sc = rowsToSpeakers(rows);
      return { ...d, speaker_configs: sc };
    });
  };

  return (
    <section
      className="vp-edit-form"
      role="region"
      aria-label={`Editing preset ${presetName}`}
    >
      <label>
        <span>Name</span>
        <input
          ref={nameRef}
          type="text"
          value={draft.name}
          onChange={(e) => update((d) => ({ ...d, name: e.target.value }))}
          disabled={busy}
        />
      </label>

      <label>
        <span>Description</span>
        <input
          type="text"
          value={draft.description ?? ''}
          onChange={(e) =>
            update((d) => ({
              ...d,
              description: e.target.value.length > 0 ? e.target.value : undefined,
            }))
          }
          disabled={busy}
        />
      </label>

      <label>
        <span>Base voice</span>
        <select
          value={draft.base_voice}
          onChange={(e) =>
            update((d) => ({ ...d, base_voice: e.target.value }))
          }
          disabled={busy}
        >
          {VOICES.map((v) => (
            <option key={v.name} value={v.name}>
              {v.name} ({v.description})
            </option>
          ))}
        </select>
      </label>

      <fieldset>
        <legend>Director notes</legend>
        {(['tone', 'pace', 'accent', 'energy', 'reverb', 'emotion'] as const).map(
          (field) => (
            <label key={field}>
              <span>
                {field.charAt(0).toUpperCase() + field.slice(1)}
              </span>
              <input
                type="text"
                value={draft.director_notes?.[field] ?? ''}
                onChange={(e) => updateDN(field, e.target.value)}
                disabled={busy}
              />
            </label>
          ),
        )}
      </fieldset>

      <fieldset>
        <legend>Pronunciation</legend>
        {pronRows.length === 0 ? (
          <div className="muted small">No pronunciation entries.</div>
        ) : (
          pronRows.map((row, i) => (
            <div key={i} className="vp-kv-row">
              <input
                type="text"
                placeholder="token"
                value={row.token}
                onChange={(e) => {
                  const copy = pronRows.slice();
                  copy[i] = { ...row, token: e.target.value };
                  commitPronRows(copy);
                }}
                disabled={busy}
              />
              <span className="muted small">→</span>
              <input
                type="text"
                placeholder="phonetic-hint"
                value={row.hint}
                onChange={(e) => {
                  const copy = pronRows.slice();
                  copy[i] = { ...row, hint: e.target.value };
                  commitPronRows(copy);
                }}
                disabled={busy}
              />
              <button
                type="button"
                className="vp-row-remove"
                onClick={() =>
                  commitPronRows(pronRows.filter((_, j) => j !== i))
                }
                disabled={busy}
                aria-label={`Remove pronunciation ${row.token}`}
              >
                −
              </button>
            </div>
          ))
        )}
        <button
          type="button"
          onClick={() =>
            commitPronRows([...pronRows, { token: '', hint: '' }])
          }
          disabled={busy}
        >
          + Add pronunciation
        </button>
      </fieldset>

      <fieldset>
        <legend>Speakers (optional — 2+ enables multi-speaker)</legend>
        {speakerRows.length === 0 ? (
          <div className="muted small">Single-voice mode.</div>
        ) : (
          speakerRows.map((row, i) => (
            <div key={i} className="vp-speaker-row">
              <input
                type="text"
                placeholder="name"
                value={row.name}
                onChange={(e) => {
                  const copy = speakerRows.slice();
                  copy[i] = { ...row, name: e.target.value };
                  commitSpeakerRows(copy);
                }}
                disabled={busy}
              />
              <select
                value={row.voice}
                onChange={(e) => {
                  const copy = speakerRows.slice();
                  copy[i] = { ...row, voice: e.target.value };
                  commitSpeakerRows(copy);
                }}
                disabled={busy}
              >
                <option value="">(pick voice)</option>
                {VOICES.map((v) => (
                  <option key={v.name} value={v.name}>
                    {v.name}
                  </option>
                ))}
              </select>
              <input
                type="text"
                placeholder="director hint (optional)"
                value={row.director}
                onChange={(e) => {
                  const copy = speakerRows.slice();
                  copy[i] = { ...row, director: e.target.value };
                  commitSpeakerRows(copy);
                }}
                disabled={busy}
              />
              <button
                type="button"
                className="vp-row-remove"
                onClick={() =>
                  commitSpeakerRows(speakerRows.filter((_, j) => j !== i))
                }
                disabled={busy}
                aria-label={`Remove speaker ${row.name || '#' + (i + 1)}`}
              >
                −
              </button>
            </div>
          ))
        )}
        <button
          type="button"
          onClick={() =>
            commitSpeakerRows([
              ...speakerRows,
              { name: '', voice: '', director: '' },
            ])
          }
          disabled={busy}
        >
          + Add speaker
        </button>
        {speakerRows.length === 1 ? (
          <div className="muted small">
            Single-entry lists are stripped on save. Add a second speaker to
            enable multi-speaker mode, or remove this row to stay single-voice.
          </div>
        ) : null}
      </fieldset>

      <label>
        <span>Sample rate hint</span>
        <input
          type="number"
          value={draft.sample_rate_hint ?? ''}
          onChange={(e) => {
            const n = e.target.value === '' ? undefined : Number(e.target.value);
            update((d) => ({
              ...d,
              sample_rate_hint: n,
            }));
          }}
          disabled={busy}
          min={1}
        />
      </label>

      <label>
        <span>Version</span>
        <input
          type="text"
          value={draft.version ?? ''}
          onChange={(e) =>
            update((d) => ({
              ...d,
              version: e.target.value.length > 0 ? e.target.value : undefined,
            }))
          }
          disabled={busy}
        />
      </label>

      <div className="vp-edit-actions">
        <button
          type="button"
          className="btn-primary"
          onClick={onSave}
          disabled={busy || errors.length > 0}
        >
          Save
        </button>
        <button type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>

      {errors.length > 0 ? (
        <ul className="vp-edit-errors" role="alert">
          {errors.map((m, i) => (
            <li key={i}>{m}</li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
